/**
 * Shared components for Site KPI Tab
 *
 * Contains CircularProgress, PeriodToggle, and KPI comparison components.
 */
import { usePreferences } from '../../contexts/PreferencesContext'
import { formatKpiPercent } from '../../lib/kpiPercent'

/**
 * Circular Progress Component for OTIF/defect rate metrics.
 *
 * `decimals` comes from Admin Setup -> Regional -> Decimal Precision
 * (`kpi_percent_decimals`). It defaults to 1 rather than 0 so any caller that
 * does not pass it keeps this component's historical one-decimal label.
 */
export function CircularProgress({
  value,
  size = 80,
  strokeWidth = 8,
  color = '#10b981',
  decimals = 1,
}: {
  value: number
  size?: number
  strokeWidth?: number
  color?: string
  decimals?: number
}) {
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const offset = circumference - (value / 100) * circumference

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-sm font-bold">{formatKpiPercent(value, decimals)}</span>
      </div>
    </div>
  )
}

/**
 * Mini Circular Progress for compact side-by-side comparison display.
 *
 * `decimals` — see CircularProgress above; same setting, same default.
 */
export function MiniCircularProgress({
  value,
  label,
  color = '#10b981',
  decimals = 1,
}: {
  /** null = N/A (nothing closed or overdue to divide by, METRIC-DEFINITIONS Q13) */
  value: number | null
  label: string
  color?: string
  decimals?: number
}) {
  const size = 60
  const strokeWidth = 6
  const radius = (size - strokeWidth) / 2
  const circumference = radius * 2 * Math.PI
  const isNA = value === null
  const offset = isNA ? circumference : circumference - (value / 100) * circumference

  return (
    <div className="flex flex-col items-center">
      <div className="text-[10px] font-medium text-muted-foreground mb-1">{label}</div>
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={isNA ? '#9ca3af' : color}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold">{formatKpiPercent(value, decimals)}</span>
        </div>
      </div>
    </div>
  )
}

/**
 * CR-6267: one customer-configured OTIF colour band.
 *
 * Replaces the former fixed `{excellent, good, poor}` triple, which could
 * only ever express three tiers with hardcoded green/yellow/red. Bands are
 * fully customisable from Admin Setup → Performance → OTIF Settings.
 */
export interface OTIFGradeBand {
  grade: string
  label: string
  min_otif_percent: number
  max_otif_percent: number
  color: string
}

/** Ordered OTIF colour bands (any length), highest band first. */
export type DeliveryThresholds = OTIFGradeBand[]

/**
 * CR-6652: one customer-configured PPM colour band.
 *
 * Replaces the former fixed `{excellent, good, poor}` triple. Bands are
 * fully customisable from Admin Setup → Performance → PPM Settings.
 */
export interface PPMGradeBand {
  grade: string
  label: string
  min_ppm: number
  max_ppm: number
  color: string
}

/** Ordered PPM colour bands (any length), lowest band first. */
export type QualityGradeBands = PPMGradeBand[]

/** Colour used when a company has no bands configured at all. */
const UNGRADED_COLOR = '#9CA3AF'

/**
 * Get the colour for a delivery metric (OTIF/OTD) from the configured bands.
 *
 * Resolves by lower bound — the highest band whose `min_otif_percent` the
 * value reaches — so a gap between customer-edited bands can never leave a
 * real figure uncoloured. Matches the backend `OTIFGradeThreshold.resolve`.
 *
 * @param value - The OTIF/OTD percentage to colour
 * @param bands - Configured bands in any order
 * @returns Hex colour for the matching band
 */
export function getDeliveryColor(value: number, bands: DeliveryThresholds): string {
  if (!bands || bands.length === 0) return UNGRADED_COLOR
  const ordered = [...bands].sort((a, b) => b.min_otif_percent - a.min_otif_percent)
  const match = ordered.find((b) => value >= b.min_otif_percent)
  return (match ?? ordered[ordered.length - 1]).color
}

/**
 * Get the band a delivery metric falls into, for labels and tooltips.
 *
 * @param value - The OTIF/OTD percentage
 * @param bands - Configured bands in any order
 * @returns The matching band, or null when none are configured
 */
export function getDeliveryBand(value: number, bands: DeliveryThresholds): OTIFGradeBand | null {
  if (!bands || bands.length === 0) return null
  const ordered = [...bands].sort((a, b) => b.min_otif_percent - a.min_otif_percent)
  return ordered.find((b) => value >= b.min_otif_percent) ?? ordered[ordered.length - 1]
}

/**
 * Get the colour for a PPM value from the configured bands.
 *
 * CR-6652: Resolves by range — finds the band where min_ppm <= value < max_ppm.
 * Lower PPM = better quality, so bands are sorted ascending.
 *
 * @param value - The PPM value to colour
 * @param bands - Configured bands in any order
 * @returns Hex colour for the matching band
 */
export function getQualityColorFromBands(value: number, bands: QualityGradeBands): string {
  if (!bands || bands.length === 0) return UNGRADED_COLOR
  const ordered = [...bands].sort((a, b) => a.min_ppm - b.min_ppm)
  const match = ordered.find((b) => value >= b.min_ppm && value < b.max_ppm)
  // If no exact match, use the highest band (worst quality) for values above all ranges
  return (match ?? ordered[ordered.length - 1]).color
}

/**
 * Get the band a PPM value falls into, for labels and tooltips.
 *
 * @param value - The PPM value
 * @param bands - Configured bands in any order
 * @returns The matching band, or null when none are configured
 */
export function getQualityBand(value: number, bands: QualityGradeBands): PPMGradeBand | null {
  if (!bands || bands.length === 0) return null
  const ordered = [...bands].sort((a, b) => a.min_ppm - b.min_ppm)
  return ordered.find((b) => value >= b.min_ppm && value < b.max_ppm) ?? ordered[ordered.length - 1]
}

/** A card period: the latest month, or the trailing 3 or 12 months. */
export type KPIPeriod = 1 | 3 | 12

/**
 * CR-6284: what makes a card period clickable, as the Appeals tiles are —
 * pointer, button role, and Enter / Space from the keyboard.
 *
 * @param months - the period the cell shows
 * @param onSelect - the card's drill-down, if it has one
 * @returns the props to spread on the cell (none when there is no drill-down)
 */
function periodDrillProps(months: KPIPeriod, onSelect?: (months: KPIPeriod) => void) {
  if (!onSelect) return {}
  return {
    role: 'button',
    tabIndex: 0,
    onClick: () => onSelect(months),
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(months) }
    },
  }
}

/**
 * OTIF Comparison Card - displays Month, 3 Month, and 12 Month side-by-side.
 * CR-6267: coloured from the company's configured OTIF grade bands.
 * CR-6284: with `onSelect`, each period opens its OTIF drill-down.
 */
export function OTIFComparisonCard({
  values,
  thresholds = [],
  onSelect,
}: {
  values: { month: number | null; threeMonth: number | null; twelveMonth: number | null }
  thresholds?: DeliveryThresholds
  onSelect?: (months: KPIPeriod) => void
}) {
  // Read the setting here rather than threading it through every caller —
  // the primitives above stay presentational.
  const { preferences } = usePreferences()
  const kpiDecimals = preferences.kpi_percent_decimals ?? 0
  const periods: Array<[KPIPeriod, string, number | null]> = [
    [1, 'MONTH', values.month], [3, '3 MONTH', values.threeMonth], [12, '12 MONTH', values.twelveMonth],
  ]
  return (
    <div className="flex justify-around items-center py-2">
      {periods.map(([months, label, value]) => {
        const ring = (
          <MiniCircularProgress
            key={label}
            value={value}
            label={label}
            color={value === null ? '#9ca3af' : getDeliveryColor(value, thresholds)}
            decimals={kpiDecimals}
          />
        )
        return onSelect ? (
          <div key={label} className="rounded-md p-1 cursor-pointer transition-all hover:bg-muted" {...periodDrillProps(months, onSelect)}>
            {ring}
          </div>
        ) : ring
      })}
    </div>
  )
}

/**
 * Spend Comparison Card - displays Month, 3 Month, and 12 Month in horizontal layout.
 * CR-6284: with `onSelect`, each period opens its Spend drill-down.
 */
export function SpendComparisonCard({
  values,
  formatCurrency,
  onSelect,
}: {
  values: { month: number; threeMonth: number; twelveMonth: number }
  formatCurrency: (value: number) => string
  onSelect?: (months: KPIPeriod) => void
}) {
  const periods: Array<[KPIPeriod, string, number]> = [
    [1, 'MONTH', values.month], [3, '3 MONTH', values.threeMonth], [12, '12 MONTH', values.twelveMonth],
  ]
  const clickable = onSelect ? ' cursor-pointer transition-all hover:bg-blue-100' : ''
  return (
    <div className="w-full py-2">
      <div className="grid grid-cols-3 gap-1">
        {periods.map(([months, label, value]) => (
          <div
            key={label}
            className={`flex flex-col items-center justify-center py-2 px-1 bg-blue-50 rounded-md${clickable}`}
            {...periodDrillProps(months, onSelect)}
          >
            <div className="text-[9px] font-medium text-muted-foreground mb-1">{label}</div>
            <div className="text-sm font-bold text-blue-600 tabular-nums text-center break-all leading-tight">{formatCurrency(value)}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Quality Metric (PPM) Comparison Card - displays Month, 3 Month, and 12 Month in horizontal layout.
 * Updated to use three-tier color thresholds from Performance Settings
 */
export function QualityMetricComparisonCard({
  values,
  target,
  bands = [],
}: {
  values: { month: number; threeMonth: number; twelveMonth: number }
  /** null = no target configured (CR-7714 FE-20) */
  target: number | null
  /** CR-6652: configurable PPM colour bands (CR-6269: the only colour source) */
  bands?: QualityGradeBands
}) {
  const getColor = (value: number) => getQualityColorFromBands(value, bands)

  // For background, derive from the same color with opacity
  const getBgStyle = (value: number) => ({
    backgroundColor: `${getColor(value)}15`, // 15 = ~8% opacity in hex
  })

  return (
    <div className="w-full py-2">
      <div className="grid grid-cols-3 gap-1">
        <div className="flex flex-col items-center justify-center py-2 px-1 rounded-md" style={getBgStyle(values.month)}>
          <div className="text-[9px] font-medium text-muted-foreground mb-1">MONTH</div>
          <div className="text-sm font-bold tabular-nums text-center" style={{ color: getColor(values.month) }}>
            {values.month.toLocaleString()}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-2 px-1 rounded-md" style={getBgStyle(values.threeMonth)}>
          <div className="text-[9px] font-medium text-muted-foreground mb-1">3 MONTH</div>
          <div className="text-sm font-bold tabular-nums text-center" style={{ color: getColor(values.threeMonth) }}>
            {values.threeMonth.toLocaleString()}
          </div>
        </div>
        <div className="flex flex-col items-center justify-center py-2 px-1 rounded-md" style={getBgStyle(values.twelveMonth)}>
          <div className="text-[9px] font-medium text-muted-foreground mb-1">12 MONTH</div>
          <div className="text-sm font-bold tabular-nums text-center" style={{ color: getColor(values.twelveMonth) }}>
            {values.twelveMonth.toLocaleString()}
          </div>
        </div>
      </div>
      <div className="text-[10px] text-center text-muted-foreground mt-2">
        Target: {target === null ? 'not set' : target.toLocaleString()}
      </div>
    </div>
  )
}

/**
 * Period toggle button component for switching between time periods.
 */
export function PeriodToggle({
  options,
  selected,
  onChange,
}: {
  options: string[]
  selected: string
  onChange: (value: string) => void
}) {
  return (
    <div className="flex border rounded overflow-hidden text-xs">
      {options.map((option) => (
        <button
          key={option}
          onClick={() => onChange(option)}
          className={`px-2 py-1 ${
            selected === option
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-600 hover:bg-gray-100'
          }`}
        >
          {option}
        </button>
      ))}
    </div>
  )
}

/**
 * Helper function to parse month string to date range for API filtering.
 */
export const parseMonthToDateRange = (monthStr: string): { date_from: string; date_to: string } | null => {
  try {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const parts = monthStr.trim().split(' ')
    const monthIndex = monthNames.indexOf(parts[0])
    if (monthIndex === -1) return null
    let year: number
    if (parts.length >= 2 && parts[1]) {
      year = parseInt(parts[1])
      if (year < 100) year += 2000
    } else {
      year = new Date().getFullYear()
    }
    // CR-4671: Format in local timezone — see dashboardUtils.ts for context.
    const startDate = new Date(year, monthIndex, 1)
    const endDate = new Date(year, monthIndex + 1, 0)
    const fmt = (d: Date): string =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    return {
      date_from: fmt(startDate),
      date_to: fmt(endDate),
    }
  } catch {
    return null
  }
}
